/* Card's two colour channels (C7, ADR-0092 amendment). `tone` is a semantic
   wash: the soft fill and the tone border from lib/tones.ts, under content
   that keeps its own text colours. `tint` is the module identity: the stripe
   and the hover channel from lib/tints.ts. A card takes one or the other
   (CardProps refuses both), and this file holds no hue of its own. */
import { TONES, type StatusTone } from "../../lib/tones.js";
import { MODULE_TINTS, type ModuleTint } from "../../lib/tints.js";

/** Identity channel for a card: a module tint (ADR-0092, lib/tints.ts). */
export type CardTint = ModuleTint;

/** Where the colour bar sits: across the top, or down the inline-start edge
 *  (the right edge under RTL). */
export type CardStripe = "top" | "start";

/** A semantic tone or a decorative tint, never both: the type refuses the
 *  pair, so no rule has to pick one over the other. */
export type CardColourProps =
  | {
      /** A toned panel of mixed content: the tone's soft fill and border
       *  (lib/tones.ts). The content keeps its own text colours. A notice
       *  (icon, message, dismiss) is an Alert, not a toned card. */
      tone?: StatusTone;
      tint?: never;
      /** The colour bar in the tone's colour. Default: none. */
      stripe?: CardStripe;
    }
  | {
      tone?: never;
      /** Identity stripe plus the hover tint on that channel (lib/tints.ts).
       *  Implies the tint, not the lift — compose with `interactive` for
       *  both. */
      tint?: CardTint;
      /** Where the identity stripe sits. Default `top`. */
      stripe?: CardStripe;
    };

/* `top` is today's 3px tint stripe markup. `start` is a 4px bar over the
   card's inline-start edge: full height, and `start-0` flips it under RTL. */
const STRIPE_PLACEMENT: Record<CardStripe, string> = {
  top: "block h-0.75 w-full",
  start: "absolute inset-y-0 start-0 w-1",
};

/** The root's colour classes: a tone's wash and border, or a tint's hover
 *  channel with the `hover-tint` utility. `hover` false drops the utility
 *  (a selected or dragged card) and keeps the channel. */
export function cardColourClasses(
  tone: StatusTone | undefined,
  tint: CardTint | undefined,
  hover: boolean,
) {
  if (tone) return [TONES[tone].fill, TONES[tone].border];
  if (!tint) return undefined;
  return [MODULE_TINTS[tint].hoverTint, hover && "hover-tint"];
}

/** The colour bar, or null. `stripe` places it; unset, a tint gets today's
 *  top stripe and a tone gets none. It paints the tone's or tint's
 *  full-strength colour; an uncoloured card has no bar. */
export function cardStripe(
  tone: StatusTone | undefined,
  tint: CardTint | undefined,
  stripe: CardStripe | undefined,
): { placement: CardStripe; className: string } | null {
  let colour: string | undefined;
  if (tone) colour = TONES[tone].dot;
  else if (tint) colour = MODULE_TINTS[tint].stripe;
  const placement = stripe ?? (tint ? "top" : undefined);
  if (!colour || !placement) return null;
  return { placement, className: `${STRIPE_PLACEMENT[placement]} ${colour}` };
}
